import { useCallback, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { useMutation, useQuery } from "@tanstack/react-query";
import axios from "axios";
import { Button } from "@/design-system/components/react";
import { authService } from "../../services/auth";
import { getApiBaseUrl } from "../../config/api";
import { useFindingSliceWindow, type DisplayWindow } from "../../hooks/useFindingSliceWindow";
import { presetForFinding } from "../../utils/findingWindow";
import { WINDOW_PRESETS, type PresetConfig } from "../../constants/windowPresets";
import { RADIUS_EXPANDED } from "../FindingPreviewModal";
import { CINE_INTERVAL_MS, pingPongIndex } from "../../utils/cine";
import { formatFindingName } from "../../utils/textFormatters";
import { fetchOriginalReport } from "../../utils/originalReport";
import {
  FaCheck,
  FaChevronLeft,
  FaChevronRight,
  FaDownload,
  FaEdit,
  FaSpinner,
} from "../../utils/icons";
import type {
  AssessmentManifest,
  SelectedUnit,
  ReturnedImage,
  UnitReview,
  ImageRating,
} from "../../types/keyImageReview.generated";

export interface ReviewEvent {
  sample_id: string;
  revision: number;
  recorded_at_utc: string;
  review: UnitReview;
}
export interface AssessmentView {
  manifest: AssessmentManifest;
  manifest_sha256: string;
  current: Record<string, ReviewEvent>;
  history: ReviewEvent[];
}

type Rating = ImageRating["rating"];
const RATINGS: Record<Rating, string> = {
  A: "Finding visible and useful",
  B: "Finding visible, but slice is suboptimal",
  C: "Valid image, finding not visible",
  D: "Image missing or invalid (e.g., won’t load or wrong study)",
};
const RATING_KEYS = Object.keys(RATINGS) as Rating[];
type ReferenceStatus = NonNullable<UnitReview["reference_support_status"]>;
const REFERENCE_OPTIONS: [ReferenceStatus, string][] = [
  ["supported", "Supported"],
  ["unsupported", "Unsupported"],
  ["unclear", "Unclear"],
  ["unassessable", "Can’t assess"],
];
const fieldClass =
  "w-full rounded-[2px] border border-border-strong bg-surface p-2 text-sm text-fg";
const labelClass = "block text-sm text-fg";

function errorText(error: unknown): string {
  if (axios.isAxiosError(error)) {
    if (typeof error.response?.data?.detail === "string") return error.response.data.detail;
    if (error.response?.status === 422) return "Check the required ratings and exception details.";
  }
  return "Save was not confirmed. Your entries are still here; retry the same save.";
}

function findingName(unit: SelectedUnit): string {
  return formatFindingName(unit.finding.split("/").at(-1) ?? unit.finding);
}

/** Shortcuts are ignored while typing, as in the labeling panel. */
function isTypingTarget(target: EventTarget | null): boolean {
  return (
    (target instanceof Element && !!target.closest("[data-shortcut-guard]")) ||
    target instanceof HTMLInputElement ||
    target instanceof HTMLTextAreaElement ||
    target instanceof HTMLSelectElement ||
    (target instanceof HTMLElement && target.isContentEditable)
  );
}

function Kbd({ children }: { children: string }) {
  return (
    <kbd className="inline-flex min-w-5 items-center justify-center rounded-[2px] border border-border-strong bg-surface-2 px-1 font-mono text-[11px] leading-5 text-fg-muted">
      {children}
    </kbd>
  );
}

interface ImageAvailability {
  canRate: (rating: Rating) => boolean;
  displayStatus: ImageRating["display_status"];
  showKeySlice: () => void;
  cycleWindow: () => void;
}

// Reuses the viewer's shared presets; the finding default comes from presetForFinding.
const REVIEW_PRESET_IDS = ["soft_tissue", "lung", "bone", "brain"] as const;
const REVIEW_PRESETS = REVIEW_PRESET_IDS.map(
  (id) => WINDOW_PRESETS.find((preset) => preset.id === id) as PresetConfig,
);
/** HU per pixel of drag, per axis. */
const DRAG_WIDTH_PER_PX = 4;
const DRAG_CENTER_PER_PX = 2;

type WindowChoice =
  | { kind: "default" }
  | { kind: "preset"; preset: PresetConfig }
  | { kind: "custom"; center: number; width: number };

function windowLabel(choice: WindowChoice, fallback: PresetConfig): string {
  const { label, center, width } =
    choice.kind === "custom"
      ? { label: "Custom", center: choice.center, width: choice.width }
      : choice.kind === "preset"
        ? choice.preset
        : { ...fallback, label: `Default (${fallback.label.toLowerCase()})` };
  return `${label} W${Math.round(width)}/L${Math.round(center)}`;
}

function ReturnedImageRating({
  unit,
  image,
  deviceId,
  selected,
  disabled,
  focused,
  onFocus,
  onChange,
  onAvailability,
  quick,
  onContextViewed,
}: {
  quick: boolean;
  onContextViewed: () => void;
  unit: SelectedUnit;
  image: ReturnedImage | null;
  deviceId: string;
  selected?: ImageRating;
  disabled: boolean;
  focused: boolean;
  onFocus: () => void;
  onChange: (rating: ImageRating) => void;
  onAvailability: (ordinal: number, availability: ImageAvailability) => void;
}) {
  const resolved = image?.resolves_to_analyzed_study === true;
  // Display window for this image only. It is presentation state: it never enters
  // the payload or marks the form dirty, and resets on the next finding (remount).
  const findingDefault = presetForFinding(unit.finding);
  const [windowChoice, setWindowChoice] = useState<WindowChoice>({ kind: "default" });
  const displayWindow: DisplayWindow | null =
    windowChoice.kind === "default"
      ? null
      : windowChoice.kind === "preset"
        ? { center: windowChoice.preset.center, width: windowChoice.preset.width }
        : { center: windowChoice.center, width: windowChoice.width };
  const effectiveWindow = displayWindow ?? {
    center: findingDefault.center,
    width: findingDefault.width,
  };
  const cycleWindow = useCallback(() => {
    setWindowChoice((choice) => {
      const index =
        choice.kind === "preset"
          ? REVIEW_PRESETS.findIndex((preset) => preset.id === choice.preset.id)
          : -1;
      return index + 1 < REVIEW_PRESETS.length
        ? { kind: "preset", preset: REVIEW_PRESETS[index + 1] }
        : { kind: "default" };
    });
  }, []);
  const drag = useRef<{ x: number; y: number; center: number; width: number } | null>(null);
  const { frames, keyIndex, loading, error, contextError } = useFindingSliceWindow(
    unit.study_id,
    deviceId,
    unit.finding,
    image?.canonical_sop_uid ?? null,
    // Exact multi-frame references keep single-frame display; otherwise show context.
    !image?.frame_number ? RADIUS_EXPANDED : 0,
    resolved,
    {
      studyUid: unit.canonical_study_uid,
      seriesUid: image?.canonical_series_uid,
      frameNumber: image?.frame_number,
    },
    displayWindow,
  );
  // Practice autoplays; the scored read opens paused on the model-selected slice.
  const [playing, setPlaying] = useState(quick);
  const [tick, setTick] = useState(0);
  useEffect(() => setTick(keyIndex), [keyIndex, frames.length]);
  useEffect(() => {
    if (!playing || loading || frames.length < 2 || disabled) return;
    const timer = window.setInterval(() => setTick((value) => value + 1), CINE_INTERVAL_MS);
    return () => window.clearInterval(timer);
  }, [playing, loading, frames.length, disabled]);
  const cursor = pingPongIndex(tick, frames.length);
  useEffect(() => {
    // Viewing a non-key slice records context consultation. In the paused scored
    // view only reader actions count (the scrubber below); a transient index
    // mismatch while the context window is still loading must not.
    if ((quick || playing) && !loading && frames[cursor] && cursor !== keyIndex) onContextViewed();
  }, [quick, playing, loading, frames, cursor, keyIndex, onContextViewed]);
  const showKeySlice = useCallback(() => {
    setPlaying(false);
    setTick(keyIndex);
  }, [keyIndex]);
  const ordinal = image?.ordinal ?? 0;
  const rendered = resolved && !!frames[keyIndex] && !loading;
  const showingKeySlice = rendered && cursor === keyIndex;
  const displayStatus = !image ? "not_returned" : rendered ? "rendered" : "unavailable";
  const ratingDisabled = useCallback(
    (rating: Rating) => (loading && resolved) || (rating !== "D" && !rendered),
    [loading, resolved, rendered],
  );
  useEffect(() => {
    onAvailability(ordinal, {
      canRate: (rating) => !ratingDisabled(rating),
      displayStatus,
      showKeySlice,
      cycleWindow,
    });
  }, [ordinal, ratingDisabled, displayStatus, showKeySlice, cycleWindow, onAvailability]);
  return (
    <fieldset
      disabled={disabled}
      onFocus={onFocus}
      onPointerDown={onFocus}
      className={`min-w-0 rounded-[2px] border bg-surface p-3 transition-colors ${
        focused ? "border-signal-stroke" : "border-border"
      }`}
    >
      <legend className="sr-only">
        {image ? `Returned image ${ordinal}` : "No returned image"}
      </legend>
      <div className="mb-2 flex items-center justify-between gap-2 text-xs">
        <span aria-hidden="true" className="font-medium text-fg">
          {image ? `Returned image ${ordinal}` : "No returned image"}
        </span>
        {rendered && (
          <span
            aria-live={playing ? "off" : "polite"}
            className={`rounded-[2px] px-2 py-0.5 ${
              showingKeySlice
                ? "bg-signal-tint font-semibold text-signal-text-tint"
                : "text-fg-muted"
            }`}
          >
            {cursor === keyIndex
              ? "Model-selected slice"
              : `Nearby slice (${cursor - keyIndex > 0 ? "+" : ""}${cursor - keyIndex})`}
          </span>
        )}
      </div>
      <div
        data-testid={`image-surface-${ordinal}`}
        title={rendered ? "Drag to adjust window: horizontal = width, vertical = level" : undefined}
        onPointerDown={(event) => {
          if (!rendered) return;
          // Suppress the browser's native image drag, which would steal the pointer.
          event.preventDefault();
          drag.current = {
            x: event.clientX,
            y: event.clientY,
            center: effectiveWindow.center,
            width: effectiveWindow.width,
          };
          event.currentTarget.setPointerCapture?.(event.pointerId);
        }}
        onPointerMove={(event) => {
          const start = drag.current;
          if (!start) return;
          const width = Math.max(1, start.width + (event.clientX - start.x) * DRAG_WIDTH_PER_PX);
          const center = start.center - (event.clientY - start.y) * DRAG_CENTER_PER_PX;
          setWindowChoice({ kind: "custom", center, width });
        }}
        onPointerUp={() => {
          drag.current = null;
        }}
        onPointerCancel={() => {
          drag.current = null;
        }}
        className={`flex aspect-square max-h-[62vh] w-full touch-none items-center justify-center overflow-hidden rounded-[2px] bg-image-void ${rendered ? "cursor-crosshair" : ""} ${`ring-2 ${showingKeySlice ? "ring-signal" : "ring-transparent"}`}`}
      >
        {rendered ? (
          <img
            src={frames[cursor]}
            alt={`Returned image ${ordinal} for ${unit.study_pseudonym}`}
            draggable={false}
            className="h-full w-full object-contain"
          />
        ) : (
          <p role="status" className="max-w-sm p-6 text-center text-sm text-fg-muted">
            {resolved && loading
              ? "Loading selected image…"
              : !image
                ? "The device returned no key image for this finding."
                : error || image.resolution_note || "This returned image is unavailable."}
          </p>
        )}
      </div>
      {rendered && (
        <div className="mt-2 space-y-2 text-sm">
          {contextError && (
            <p role="note" className="text-xs text-fg-muted">
              Some nearby slices could not be loaded. The model-selected slice is unaffected.
            </p>
          )}
          {frames.length > 1 && (
            <div className="flex items-center gap-3">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setPlaying(!playing)}
              >
                {playing ? "Pause" : "Play"}
              </Button>
              <input
                type="range"
                aria-label={`Image ${ordinal} slice`}
                min={0}
                max={frames.length - 1}
                value={cursor}
                className="min-w-0 flex-1 accent-signal"
                onChange={(event) => {
                  setPlaying(false);
                  const value = Number(event.target.value);
                  setTick(value);
                  if (value !== keyIndex) onContextViewed();
                }}
              />
              <Button type="button" variant="outline" size="sm" onClick={showKeySlice}>
                Key slice
              </Button>
            </div>
          )}
        </div>
      )}
      {rendered && (
        <div
          role="group"
          aria-label={`Window for returned image ${ordinal}`}
          className="mt-2 flex flex-wrap items-center gap-1.5 text-[11px]"
        >
          {[{ id: "default", label: "Default (finding)" }, ...REVIEW_PRESETS].map((option) => {
            const active =
              option.id === "default"
                ? windowChoice.kind === "default"
                : windowChoice.kind === "preset" && windowChoice.preset.id === option.id;
            return (
              <button
                key={option.id}
                type="button"
                aria-pressed={active}
                onClick={() =>
                  setWindowChoice(
                    option.id === "default"
                      ? { kind: "default" }
                      : { kind: "preset", preset: option as PresetConfig },
                  )
                }
                className={`rounded-[2px] border px-2 py-0.5 transition-colors ${
                  active
                    ? "border-signal bg-signal-tint text-fg"
                    : "border-border bg-surface-2 text-fg-muted hover:text-fg"
                }`}
              >
                {option.id === "soft_tissue" ? "Soft tissue" : option.label}
              </button>
            );
          })}
          {windowChoice.kind === "custom" && (
            <button
              type="button"
              onClick={() => setWindowChoice({ kind: "default" })}
              className="rounded-[2px] border border-border px-2 py-0.5 text-fg-muted hover:text-fg"
            >
              Reset
            </button>
          )}
          <span data-testid={`window-label-${ordinal}`} className="ml-auto font-mono text-fg-dim">
            {windowLabel(windowChoice, findingDefault)}
          </span>
        </div>
      )}
      <p className="mt-2 text-[11px] text-fg-dim">
        Informational preview · not for primary diagnosis
      </p>
      {rendered && frames.length > 1 && (
        <p className="mt-3 text-xs font-medium text-fg">Rate the model-selected slice</p>
      )}
      <div
        role="radiogroup"
        tabIndex={-1}
        data-rating-ordinal={ordinal}
        aria-label={`Rating for ${image ? `returned image ${ordinal}` : "no returned image"}`}
        className="mt-3 grid grid-cols-2 gap-2"
      >
        {RATING_KEYS.map((rating) => {
          const isChecked = selected?.rating === rating;
          const isDisabled = ratingDisabled(rating);
          return (
            <label
              key={rating}
              className={`flex cursor-pointer items-start gap-2 rounded-[2px] border px-2.5 py-2 text-sm transition-colors ${
                isChecked
                  ? "border-signal bg-signal-tint text-fg"
                  : "border-border bg-surface-2 text-fg-muted hover:border-border-strong hover:text-fg"
              } ${isDisabled ? "cursor-not-allowed opacity-40 hover:border-border hover:text-fg-muted" : ""}`}
            >
              <input
                type="radio"
                className="mt-0.5 shrink-0 accent-signal"
                name={`image-${ordinal}`}
                aria-label={`${rating} — ${RATINGS[rating]}`}
                checked={isChecked}
                disabled={isDisabled}
                onChange={() => {
                  showKeySlice();
                  onChange({ ordinal, rating, display_status: displayStatus });
                }}
              />
              <span
                aria-hidden="true"
                className={`mt-px font-mono text-xs font-semibold ${isChecked ? "text-signal-text-tint" : "text-fg"}`}
              >
                {rating}
              </span>
              <span aria-hidden="true" className="leading-snug">
                {RATINGS[rating]}
              </span>
            </label>
          );
        })}
      </div>
      <div className="mt-3 flex items-center justify-between gap-3">
        <span />
        {image && (
          <details className="min-w-0 text-[11px] text-fg-dim">
            <summary className="cursor-pointer text-right">Image reference</summary>
            <p className="mt-1 break-all">
              {image.sop_uid || "Invalid / missing identifier"}
              {image.frame_number ? ` · frame ${image.frame_number}` : ""}
            </p>
          </details>
        )}
      </div>
    </fieldset>
  );
}

/** The references builder's placeholder when no report sentence could be anchored. */
const UNANCHORED_PREFIX = "Report-derived reference; no report sentence could be anchored";

/** Split report text into plain/highlighted parts by exact anchor matches. No HTML. */
export function highlightAnchor(
  text: string,
  anchor: string,
): { text: string; highlighted: boolean }[] {
  const pieces = anchor.startsWith(UNANCHORED_PREFIX)
    ? []
    : // Multi-span anchors are joined with " … "; each span is matched exactly.
      anchor
        .split(" … ")
        .map((piece) => piece.trim())
        .filter((piece) => piece.length > 0);
  const ranges: [number, number][] = [];
  for (const piece of pieces) {
    const start = text.indexOf(piece);
    if (start >= 0) ranges.push([start, start + piece.length]);
  }
  ranges.sort((a, b) => a[0] - b[0]);
  const parts: { text: string; highlighted: boolean }[] = [];
  let cursor = 0;
  for (const [start, end] of ranges) {
    if (start < cursor) continue; // overlapping span: keep the first
    if (start > cursor) parts.push({ text: text.slice(cursor, start), highlighted: false });
    parts.push({ text: text.slice(start, end), highlighted: true });
    cursor = end;
  }
  if (cursor < text.length) parts.push({ text: text.slice(cursor), highlighted: false });
  return parts;
}

function FullReportPanel({
  studyUid,
  anchor,
  token,
  open,
  onToggle,
}: {
  studyUid: string;
  anchor: string;
  token: string;
  open: boolean;
  onToggle: () => void;
}) {
  // Cached per study for the session; prev/next never refetch. Read-only: it
  // never touches the form state, so it cannot mark the finding dirty.
  const report = useQuery({
    queryKey: ["key-image-original-report", studyUid],
    queryFn: () => fetchOriginalReport(studyUid, token),
    enabled: open && !!studyUid,
    staleTime: Infinity,
    gcTime: Infinity,
    retry: false,
    refetchOnWindowFocus: false,
  });
  const text = report.data?.available ? (report.data.report_text ?? "") : "";
  return (
    <section aria-label="Full report" className="mt-3" data-shortcut-guard>
      <button
        type="button"
        aria-expanded={open}
        onClick={onToggle}
        className="flex items-center gap-2 text-xs text-fg-muted hover:text-fg"
      >
        <span
          aria-hidden="true"
          className={`inline-block transition-transform ${open ? "rotate-90" : ""}`}
        >
          <FaChevronRight className="h-2.5 w-2.5" />
        </span>
        Full report
      </button>
      {open && (
        <div className="mt-2">
          {report.isPending ? (
            <p role="status" className="flex items-center gap-2 text-xs text-fg-muted">
              <FaSpinner className="h-3 w-3 animate-spin" />
              Loading original report…
            </p>
          ) : text ? (
            <pre
              tabIndex={0}
              aria-label="Original radiologist report"
              className="max-h-64 overflow-y-auto whitespace-pre-wrap rounded-[2px] border border-border bg-surface-2 p-3 font-sans text-sm leading-relaxed text-fg-muted"
            >
              {highlightAnchor(text, anchor).map((part, n) =>
                part.highlighted ? (
                  <mark key={n} className="rounded-[2px] bg-signal-tint px-0.5 text-fg">
                    {part.text}
                  </mark>
                ) : (
                  <span key={n}>{part.text}</span>
                ),
              )}
            </pre>
          ) : (
            <p className="text-xs italic text-fg-muted">Original report unavailable.</p>
          )}
        </div>
      )}
    </section>
  );
}

function UnitForm({
  unit,
  manifest,
  manifestHash,
  saved,
  onSave,
  onDirty,
  defaultInitials,
  shortcutsEnabled,
  focusOnMount,
  token,
  reportOpen,
  onToggleReport,
}: {
  unit: SelectedUnit;
  manifest: AssessmentManifest;
  manifestHash: string;
  saved?: ReviewEvent;
  onSave: (body: UnitReview) => Promise<void>;
  onDirty: (value: boolean) => void;
  defaultInitials: string;
  shortcutsEnabled: boolean;
  focusOnMount: boolean;
  token: string;
  reportOpen: boolean;
  onToggleReport: () => void;
}) {
  const quick = manifest.purpose === "practice";
  const previous = saved?.review;
  const [ratings, setRatings] = useState<Record<number, ImageRating>>(
    Object.fromEntries(previous?.image_ratings.map((r) => [r.ordinal, r]) ?? []),
  );
  const [reference, setReference] = useState<
    NonNullable<UnitReview["reference_support_status"]> | ""
  >(previous?.reference_support_status ?? "");
  const [contextUsed, setContextUsed] = useState(previous?.context_consulted ?? false);
  const contextUsedRef = useRef(contextUsed);
  contextUsedRef.current = contextUsed;
  const changedRef = useRef<() => void>(() => {});
  // Viewing a non-key slice records context consultation. It is a real edit to
  // the saved judgment, so it goes through the same change handling as every
  // field (unsaved-work guard, cached-command invalidation), once per finding.
  const noteContextViewed = useCallback(() => {
    if (contextUsedRef.current) return;
    contextUsedRef.current = true;
    changedRef.current();
    setContextUsed(true);
  }, []);
  const [contextOnly, setContextOnly] = useState(previous?.context_only_localization ?? false);
  const [unclear, setUnclear] = useState(previous?.review_status === "unclear");
  const [reason, setReason] = useState(previous?.unclear_reason ?? "");
  const [limitations, setLimitations] = useState(previous?.image_quality_limitation ?? "");
  const [consequential, setConsequential] = useState(
    previous?.clinically_consequential_error ?? false,
  );
  const [comment, setComment] = useState(previous?.reviewer_comment ?? "");
  const [initials, setInitials] = useState(previous?.reviewer_initials ?? defaultInitials);
  const [amendment, setAmendment] = useState("");
  const [started] = useState(() => new Date().toISOString());
  const startedMonotonic = useRef(performance.now());
  const pendingCommand = useRef<UnitReview | null>(null);
  const ordinals = unit.images.length ? unit.images.map((image) => image.ordinal) : [0];
  const [focusedOrdinal, setFocusedOrdinal] = useState(ordinals[0]);
  const availability = useRef<Record<number, ImageAvailability>>({});
  const reportAvailability = useCallback((ordinal: number, value: ImageAvailability) => {
    availability.current[ordinal] = value;
  }, []);
  const complete =
    ordinals.every((ordinal) => !!ratings[ordinal]) &&
    (quick ||
      (reference &&
        initials.trim() &&
        (!(unclear || reference !== "supported") || reason.trim()) &&
        (!consequential || comment.trim()) &&
        (!saved || amendment.trim()) &&
        (!contextOnly ||
          (contextUsed &&
            !Object.values(ratings).some((r) => r.rating === "A" || r.rating === "B")))));
  const mutation = useMutation({ mutationFn: onSave, retry: false });
  // Presentation-only state. Validation above and the payload below are unchanged.
  const hasNotVisible = Object.values(ratings).some((r) => r.rating === "C" || r.rating === "D");
  const needsReason = !!(unclear || (reference && reference !== "supported")) && !reason.trim();
  const needsComment = consequential && !comment.trim();
  const [detailsOpen, setDetailsOpen] = useState(
    () => !!(reason || comment || limitations || unclear || contextOnly),
  );
  const [editingInitials, setEditingInitials] = useState(() => !initials.trim());
  const reasonRef = useRef<HTMLInputElement | null>(null);
  const commentRef = useRef<HTMLTextAreaElement | null>(null);
  const [focusField, setFocusField] = useState<"reason" | "comment" | null>(null);
  const prevNeeds = useRef({ reason: needsReason, comment: needsComment });
  useEffect(() => {
    // Open the disclosure and focus the field that validation now requires.
    const opened =
      needsReason && !prevNeeds.current.reason
        ? "reason"
        : needsComment && !prevNeeds.current.comment
          ? "comment"
          : null;
    prevNeeds.current = { reason: needsReason, comment: needsComment };
    if (opened) {
      setDetailsOpen(true);
      setFocusField(opened);
    }
  }, [needsReason, needsComment]);
  useEffect(() => {
    if (!focusField || !detailsOpen) return;
    (focusField === "reason" ? reasonRef.current : commentRef.current)?.focus();
    setFocusField(null);
  }, [focusField, detailsOpen]);
  const missing: string[] = [];
  const unrated = ordinals.filter((ordinal) => !ratings[ordinal]);
  if (unrated.length)
    missing.push(
      unrated[0] === 0
        ? "Rate the missing image (D)"
        : `Rate image${unrated.length > 1 ? "s" : ""} ${unrated.join(" and ")}`,
    );
  if (!quick) {
    if (!reference) missing.push("answer the report-reference question");
    if (needsReason) missing.push("give a reason");
    if (needsComment) missing.push("add error details");
    if (contextOnly && !contextUsed) missing.push("mark context consulted");
    if (contextOnly && Object.values(ratings).some((r) => r.rating === "A" || r.rating === "B"))
      missing.push("resolve the context-only conflict");
    if (!initials.trim()) missing.push("enter your initials");
    if (saved && !amendment.trim()) missing.push("give an amendment reason");
  }
  function chooseReference(value: ReferenceStatus) {
    changed();
    setReference(value);
  }
  function changed() {
    pendingCommand.current = null;
    onDirty(true);
    mutation.reset();
  }
  changedRef.current = changed;
  function rate(rating: ImageRating) {
    changed();
    setRatings((old) => {
      const next = { ...old, [rating.ordinal]: rating };
      // The consequential-error question only applies when an image is C or D.
      if (!Object.values(next).some((r) => r.rating === "C" || r.rating === "D"))
        setConsequential(false);
      // Move keyboard focus to the next image still awaiting a rating.
      const pending = ordinals.find((ordinal) => !next[ordinal]);
      if (pending !== undefined) setFocusedOrdinal(pending);
      return next;
    });
  }
  async function submit() {
    if (!complete) return;
    if (!pendingCommand.current)
      pendingCommand.current = {
        form_mode: quick ? "quick_practice" : "full",
        operation_id: crypto.randomUUID(),
        expected_revision: saved?.revision ?? 0,
        manifest_sha256: manifestHash,
        image_ratings: [
          ratings[ordinals[0]],
          ...ordinals.slice(1).map((ordinal) => ratings[ordinal]),
        ],
        reference_support_status: quick
          ? null
          : (reference as UnitReview["reference_support_status"]),
        context_consulted: contextUsed,
        context_only_localization: quick ? null : contextOnly,
        image_quality_limitation: quick ? "" : limitations,
        unclear_reason: quick ? "" : reason,
        clinically_consequential_error: quick ? null : consequential,
        review_status: !quick && unclear ? "unclear" : "reviewed",
        review_started_at_utc: started,
        review_seconds: Math.round((performance.now() - startedMonotonic.current) / 1000),
        reviewer_comment: comment,
        reviewer_initials: quick ? null : initials,
        amendment_reason: quick && saved ? "Updated during practice review" : amendment,
      };
    try {
      await mutation.mutateAsync(pendingCommand.current);
    } catch {
      /* Retain command for exact retry. */
    }
  }
  // Stable window listener dispatching through a ref, as in LabelingPanel (#576).
  const keyHandlerRef = useRef<(event: KeyboardEvent) => void>(() => {});
  keyHandlerRef.current = (event: KeyboardEvent) => {
    if (!shortcutsEnabled || mutation.isPending) return;
    if (event.metaKey || event.ctrlKey || event.altKey || isTypingTarget(event.target)) return;
    const key = event.key.toUpperCase();
    if (key === "W") {
      const target = availability.current[focusedOrdinal];
      if (!target) return;
      event.preventDefault();
      target.cycleWindow();
      return;
    }
    const referenceIndex = ["1", "2", "3", "4"].indexOf(event.key);
    if (!quick && referenceIndex >= 0) {
      event.preventDefault();
      chooseReference(REFERENCE_OPTIONS[referenceIndex][0]);
      return;
    }
    if (RATING_KEYS.includes(key as Rating)) {
      const target = availability.current[focusedOrdinal];
      if (!target || !target.canRate(key as Rating)) return;
      event.preventDefault();
      target.showKeySlice();
      rate({
        ordinal: focusedOrdinal,
        rating: key as Rating,
        display_status: target.displayStatus,
      });
      return;
    }
    // A focused button or link handles Enter natively; avoid a second submit.
    // Reference segments are answer controls, so Enter on them saves.
    const answer = event.target instanceof HTMLElement && !!event.target.dataset.answerControl;
    if (
      !answer &&
      (event.target instanceof HTMLButtonElement || event.target instanceof HTMLAnchorElement)
    )
      return;
    if (event.key === "Enter" && complete) {
      event.preventDefault();
      void submit();
    }
  };
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => keyHandlerRef.current(event);
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, []);
  const conflict =
    !quick &&
    contextOnly &&
    Object.values(ratings).some((r) => r.rating === "A" || r.rating === "B");
  const formRef = useRef<HTMLFormElement | null>(null);
  useEffect(() => {
    // After navigation, move focus off the case list / nav button into this
    // finding so that rating keys and Enter act on it (Enter on a focused
    // button would re-activate that button instead of saving).
    if (!focusOnMount) return;
    const target = ordinals.find((ordinal) => !ratings[ordinal]) ?? ordinals[0];
    formRef.current
      ?.querySelector<HTMLElement>(`[data-rating-ordinal="${target}"]`)
      ?.focus({ preventScroll: true });
    // Mount-only: runs once per finding (the form is keyed by finding).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return (
    <form
      ref={formRef}
      onSubmit={(event) => {
        event.preventDefault();
        void submit();
      }}
    >
      <header className="mb-4">
        <p className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-fg-dim">
          <span className="rounded-[2px] border border-border px-1.5 py-0.5 uppercase tracking-wide text-fg-muted">
            {unit.priority_category.replace(/_/g, " ")}
          </span>
          <span>{unit.anatomical_region.replace(/_/g, " ")}</span>
          <span aria-hidden="true">·</span>
          <span className="font-mono">{unit.study_pseudonym}</span>
        </p>
        <h2 className="mt-2 text-2xl font-medium tracking-tight text-fg">{findingName(unit)}</h2>
        <figure className="mt-3 border-l-2 border-signal pl-3">
          <figcaption className="text-[11px] uppercase tracking-wider text-fg-dim">
            Existing report reference
          </figcaption>
          <blockquote className="mt-1 whitespace-pre-wrap text-sm leading-relaxed text-fg">
            {unit.reference_anchor}
          </blockquote>
        </figure>
        {!quick && (
          <FullReportPanel
            studyUid={unit.analyzed_study_uid}
            anchor={unit.reference_anchor}
            token={token}
            open={reportOpen}
            onToggle={onToggleReport}
          />
        )}
      </header>
      <fieldset disabled={mutation.isPending}>
        <div className={`grid gap-4 ${unit.images.length > 1 ? "lg:grid-cols-2" : "max-w-3xl"}`}>
          {(unit.images.length ? unit.images : [null]).map((image) => (
            <ReturnedImageRating
              key={image?.ordinal ?? 0}
              unit={unit}
              quick={quick}
              onContextViewed={noteContextViewed}
              image={image}
              deviceId={manifest.device_id}
              selected={ratings[image?.ordinal ?? 0]}
              disabled={mutation.isPending}
              focused={ordinals.length > 1 && focusedOrdinal === (image?.ordinal ?? 0)}
              onFocus={() => setFocusedOrdinal(image?.ordinal ?? 0)}
              onAvailability={reportAvailability}
              onChange={rate}
            />
          ))}
        </div>
        {!quick && (
          <section
            aria-label="Finding judgments"
            className="mt-5 space-y-4 rounded-[2px] border border-border bg-surface p-4"
          >
            <div>
              <p id={`reference-${unit.sample_id}`} className="text-sm text-fg">
                Does the existing report reference support this finding?
              </p>
              <div
                role="radiogroup"
                aria-labelledby={`reference-${unit.sample_id}`}
                aria-required="true"
                className="mt-2 inline-flex flex-wrap overflow-hidden rounded-[2px] border border-border-strong"
              >
                {REFERENCE_OPTIONS.map(([value, label], n) => (
                  <button
                    key={value}
                    type="button"
                    role="radio"
                    aria-checked={reference === value}
                    data-answer-control="reference"
                    onClick={() => chooseReference(value)}
                    className={`flex items-center gap-2 border-border-strong px-3 py-1.5 text-sm transition-colors [&:not(:first-child)]:border-l ${
                      reference === value
                        ? "bg-signal-tint text-fg"
                        : "bg-surface-2 text-fg-muted hover:text-fg"
                    }`}
                  >
                    <span aria-hidden="true" className="font-mono text-[11px] text-fg-dim">
                      {n + 1}
                    </span>
                    {label}
                  </button>
                ))}
              </div>
            </div>
            {(hasNotVisible || consequential) && (
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={consequential}
                  onChange={(event) => {
                    changed();
                    setConsequential(event.target.checked);
                  }}
                />
                Clinically consequential misleading output
              </label>
            )}
            <div>
              <button
                type="button"
                aria-expanded={detailsOpen}
                onClick={() => setDetailsOpen(!detailsOpen)}
                className="flex items-center gap-2 text-sm text-fg-muted hover:text-fg"
              >
                <span
                  aria-hidden="true"
                  className={`inline-block transition-transform ${detailsOpen ? "rotate-90" : ""}`}
                >
                  <FaChevronRight className="h-2.5 w-2.5" />
                </span>
                Add details
                {!detailsOpen && contextUsed && (
                  <span className="text-xs text-fg-dim">· context consulted</span>
                )}
              </button>
              {detailsOpen && (
                <div className="mt-3 grid gap-x-6 gap-y-4 md:grid-cols-2">
                  <div className="space-y-3 text-sm">
                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={contextUsed}
                        onChange={(event) => {
                          changed();
                          setContextUsed(event.target.checked);
                          if (!event.target.checked) setContextOnly(false);
                        }}
                      />
                      Context consulted
                    </label>
                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={contextOnly}
                        onChange={(event) => {
                          changed();
                          setContextOnly(event.target.checked);
                          if (event.target.checked) setContextUsed(true);
                        }}
                      />
                      Localization established only in context
                    </label>
                    <a
                      className="inline-block text-signal-text-tint underline underline-offset-2"
                      href={`/case-viewer/${encodeURIComponent(unit.study_id)}?cohort=${encodeURIComponent(manifest.cohort_id)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={() => {
                        changed();
                        setContextUsed(true);
                      }}
                    >
                      Open full study in another tab
                    </a>
                    <p className="text-xs text-fg-dim">
                      Rate the returned images above. Context alone does not establish direct
                      visibility.
                    </p>
                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={unclear}
                        onChange={(event) => {
                          changed();
                          setUnclear(event.target.checked);
                        }}
                      />
                      Finding review remains unclear
                    </label>
                  </div>
                  <div className="space-y-3">
                    <label className={labelClass}>
                      Reason for uncertainty or unsupported reference
                      <input
                        ref={reasonRef}
                        className={`${fieldClass} mt-2`}
                        value={reason}
                        maxLength={1000}
                        onChange={(event) => {
                          changed();
                          setReason(event.target.value);
                        }}
                      />
                    </label>
                    <label className={labelClass}>
                      Image quality limitation, if any
                      <input
                        className={`${fieldClass} mt-2`}
                        value={limitations}
                        maxLength={1000}
                        onChange={(event) => {
                          changed();
                          setLimitations(event.target.value);
                        }}
                      />
                    </label>
                    <label className={labelClass}>
                      Comment / error details
                      <textarea
                        ref={commentRef}
                        className={`${fieldClass} mt-2`}
                        rows={2}
                        maxLength={4000}
                        value={comment}
                        onChange={(event) => {
                          changed();
                          setComment(event.target.value);
                        }}
                      />
                    </label>
                  </div>
                </div>
              )}
            </div>
            <div className="flex flex-wrap items-end gap-4 border-t border-border pt-3">
              {editingInitials ? (
                <label className={labelClass}>
                  Your initials
                  <input
                    className={`${fieldClass} mt-2 block max-w-40`}
                    value={initials}
                    required
                    maxLength={20}
                    onChange={(event) => {
                      changed();
                      setInitials(event.target.value);
                    }}
                  />
                </label>
              ) : (
                <p className="text-xs text-fg-muted">
                  Signed as <span className="font-medium text-fg">{initials}</span> ·{" "}
                  <button
                    type="button"
                    className="underline underline-offset-2 hover:text-fg"
                    onClick={() => setEditingInitials(true)}
                  >
                    change
                  </button>
                </p>
              )}
              {saved && (
                <label className={`${labelClass} min-w-64 flex-1`}>
                  Reason for amendment
                  <input
                    className={`${fieldClass} mt-2`}
                    value={amendment}
                    required
                    maxLength={1000}
                    onChange={(event) => {
                      changed();
                      setAmendment(event.target.value);
                    }}
                  />
                </label>
              )}
            </div>
          </section>
        )}
        {quick && (
          <label className="mt-4 block max-w-3xl text-sm text-fg">
            Comment (optional)
            <textarea
              className={`${fieldClass} mt-2`}
              rows={2}
              maxLength={4000}
              value={comment}
              onChange={(event) => {
                changed();
                setComment(event.target.value);
              }}
            />
          </label>
        )}
      </fieldset>
      {conflict && (
        <p role="alert" className="mt-3 text-sm text-warn">
          Context-only localization conflicts with an A or B returned-image rating. Recheck those
          judgments.
        </p>
      )}
      {mutation.error && (
        <p role="alert" className="mt-3 text-sm text-warn">
          {errorText(mutation.error)}
        </p>
      )}
      <div className="sticky bottom-0 z-10 -mx-5 mt-5 flex flex-wrap items-center gap-4 border-t border-border bg-bg/95 px-5 py-3 backdrop-blur md:-mx-8 md:px-8">
        <Button type="submit" disabled={!complete || mutation.isPending}>
          {mutation.isPending
            ? "Saving…"
            : mutation.isError
              ? "Retry save"
              : quick
                ? "Save and next"
                : saved
                  ? "Save amendment and continue"
                  : "Save and continue"}
        </Button>
        <p className="text-xs text-fg-dim">
          {saved
            ? `Saved revision ${saved.revision}. Earlier judgments remain in the record.`
            : missing.length > 0
              ? `${missing.join(" · ")}. No judgment is saved until you submit.`
              : "No judgment is saved until you submit."}
        </p>
        {complete && !mutation.isPending && (
          <span className="ml-auto flex items-center gap-1.5 text-xs text-fg-dim">
            <Kbd>Enter</Kbd> to save
          </span>
        )}
      </div>
    </form>
  );
}

type UnitStatus = "unsaved" | "saved" | "amended";
function unitStatus(event?: ReviewEvent): UnitStatus {
  if (!event) return "unsaved";
  return event.revision > 1 ? "amended" : "saved";
}

function CaseList({
  units,
  current,
  selectedId,
  onSelect,
  locked,
}: {
  units: SelectedUnit[];
  current: Record<string, ReviewEvent>;
  selectedId: string;
  onSelect: (sampleId: string) => void;
  /** True while a save/refresh is pending: selection cannot change. */
  locked: boolean;
}) {
  const selectedRef = useRef<HTMLButtonElement | null>(null);
  useEffect(() => {
    // Block body: newer browsers return a Promise from scrollIntoView.
    selectedRef.current?.scrollIntoView?.({ block: "nearest" });
  }, [selectedId]);
  return (
    <nav aria-label="Findings" className="min-h-0 flex-1 overflow-y-auto">
      <ol className="divide-y divide-border">
        {units.map((unit, n) => {
          const status = unitStatus(current[unit.sample_id]);
          const isCurrent = unit.sample_id === selectedId;
          return (
            <li key={unit.sample_id}>
              <button
                ref={isCurrent ? selectedRef : undefined}
                type="button"
                aria-current={isCurrent ? "true" : undefined}
                disabled={locked && !isCurrent}
                onClick={() => onSelect(unit.sample_id)}
                className={`flex w-full items-center gap-3 px-4 py-2.5 text-left transition-colors disabled:cursor-not-allowed disabled:opacity-60 ${
                  isCurrent ? "bg-signal-tint" : "hover:bg-surface-2"
                }`}
              >
                <span className="w-6 shrink-0 text-right font-mono text-[11px] tabular-nums text-fg-dim">
                  {n + 1}
                </span>
                <span className="min-w-0 flex-1">
                  <span
                    className={`block truncate text-sm ${isCurrent ? "font-medium text-fg" : "text-fg-muted"}`}
                  >
                    {findingName(unit)}
                  </span>
                  <span className="block truncate font-mono text-[10px] text-fg-dim">
                    {unit.study_pseudonym}
                  </span>
                </span>
                <span className="shrink-0" data-testid={`status-${unit.sample_id}`}>
                  {status === "unsaved" ? (
                    <span
                      role="img"
                      aria-label="Not saved"
                      className="block h-2 w-2 rounded-full border border-border-strong"
                    />
                  ) : (
                    <span role="img" aria-label={status === "amended" ? "Amended" : "Saved"}>
                      {status === "amended" ? (
                        <FaEdit className="h-3 w-3 text-signal-text-tint" />
                      ) : (
                        <FaCheck className="h-3 w-3 text-signal-text-tint" />
                      )}
                    </span>
                  )}
                </span>
              </button>
            </li>
          );
        })}
      </ol>
    </nav>
  );
}

export function KeyImageAssessment({
  data,
  token,
  onUpdated,
}: {
  data: AssessmentView;
  token: string;
  /** Refreshes the assessment; resolves to the fresh view when available. */
  onUpdated: () => Promise<AssessmentView | unknown>;
}) {
  const { manifest } = data;
  const quick = manifest.purpose === "practice";
  const [sampleId, setSampleId] = useState(
    () =>
      manifest.units.find((unit) => !data.current[unit.sample_id])?.sample_id ??
      manifest.units[0].sample_id,
  );
  const [dirty, setDirty] = useState(false);
  const [notice, setNotice] = useState("");
  const [exportError, setExportError] = useState("");
  const [sessionInitials, setSessionInitials] = useState("");
  // A save covers the POST and the refresh that follows it. Navigation is
  // blocked for its whole duration so its completion can never move away from
  // (and discard) a finding the reader selected meanwhile.
  const [saving, setSaving] = useState(false);
  const [reportOpen, setReportOpen] = useState(true);
  const [focusRequest, setFocusRequest] = useState(false);
  const selectionRef = useRef(sampleId);
  selectionRef.current = sampleId;
  const selected = manifest.units.find((unit) => unit.sample_id === sampleId)!;
  const index = manifest.units.indexOf(selected);
  const reviewed = manifest.units.filter((unit) => !!data.current[unit.sample_id]).length;
  const total = manifest.units.length;
  const allSaved = reviewed === total;
  const base = `${getApiBaseUrl()}/api/review/key-images/${encodeURIComponent(manifest.assessment_id)}`;
  const headers = { Authorization: `Bearer ${token}` };
  useEffect(() => {
    const beforeUnload = (event: BeforeUnloadEvent) => {
      if (dirty) event.preventDefault();
    };
    window.addEventListener("beforeunload", beforeUnload);
    return () => window.removeEventListener("beforeunload", beforeUnload);
  }, [dirty]);
  const noticeTimer = useRef<number | undefined>(undefined);
  useEffect(() => () => window.clearTimeout(noticeTimer.current), []);
  function flash(message: string) {
    setNotice(message);
    window.clearTimeout(noticeTimer.current);
    noticeTimer.current = window.setTimeout(() => setNotice(""), 4000);
  }
  function go(target: string) {
    if (saving || target === sampleId) return;
    if (dirty && !window.confirm("Discard unsaved changes to this finding?")) return;
    setDirty(false);
    setFocusRequest(true);
    setSampleId(target);
  }
  async function save(body: UnitReview) {
    const origin = sampleId;
    setSaving(true);
    let refreshed: unknown;
    try {
      await axios.post(`${base}/units/${encodeURIComponent(origin)}`, body, { headers });
      // A confirmed write is retained even if subsequent refresh fails. The same
      // operation remains retryable, so no duplicate review is produced.
      refreshed = await onUpdated();
    } finally {
      setSaving(false);
    }
    // Defence in depth: never act on a selection other than the one saved.
    if (selectionRef.current !== origin) return;
    setDirty(false);
    if (body.reviewer_initials) setSessionInitials(body.reviewer_initials);
    // Advance from the refreshed record, not the pre-save snapshot. If the
    // refresh did not return a view, the unit just confirmed counts as saved.
    const fresh =
      refreshed && typeof refreshed === "object" && "current" in refreshed
        ? (refreshed as AssessmentView).current
        : data.current;
    const isSaved = (id: string) => id === sampleId || !!fresh[id];
    const next =
      manifest.units.slice(index + 1).find((unit) => !isSaved(unit.sample_id)) ??
      manifest.units.slice(0, index).find((unit) => !isSaved(unit.sample_id));
    flash(`Saved ${findingName(selected)}.`);
    if (next) {
      setFocusRequest(true);
      setSampleId(next.sample_id);
    }
  }
  // Previous/next navigation with ←/→ or J/K; same stable-listener pattern.
  const navRef = useRef<(event: KeyboardEvent) => void>(() => {});
  navRef.current = (event: KeyboardEvent) => {
    if (event.metaKey || event.ctrlKey || event.altKey || isTypingTarget(event.target)) return;
    if (saving || document.querySelector('[role="dialog"]')) return;
    const key = event.key;
    const back = key === "ArrowLeft" || key === "k" || key === "K";
    const forward = key === "ArrowRight" || key === "j" || key === "J";
    if (!back && !forward) return;
    const target = manifest.units[index + (forward ? 1 : -1)];
    if (!target) return;
    event.preventDefault();
    go(target.sample_id);
  };
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => navRef.current(event);
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, []);
  async function exportReview() {
    setExportError("");
    try {
      const response = await axios.get(`${base}/export`, { headers, responseType: "blob" });
      const url = URL.createObjectURL(response.data);
      const link = document.createElement("a");
      link.href = url;
      link.download = "key-image-review.zip";
      link.click();
      URL.revokeObjectURL(url);
    } catch {
      setExportError("Export failed. Please retry.");
    }
  }
  const percent = Math.round((reviewed / total) * 100);
  return (
    <div className="flex h-full min-h-0 flex-col text-fg md:flex-row">
      <aside className="flex max-h-72 shrink-0 flex-col border-b border-border bg-bg-2 md:max-h-none md:w-72 md:border-r md:border-b-0">
        <div className="space-y-3 border-b border-border px-4 py-4">
          <div className="flex items-center justify-between gap-2">
            <p className="text-[11px] uppercase tracking-wider text-fg-dim">Key-image review</p>
            <span
              className={`rounded-[2px] px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide ${
                quick ? "bg-surface-2 text-fg-muted" : "bg-signal-tint text-signal-text-tint"
              }`}
            >
              {quick ? "Practice" : "Scored"}
            </span>
          </div>
          <h1 className="text-sm font-medium leading-snug text-fg">{manifest.title}</h1>
          <div>
            <div className="flex items-center justify-between text-xs text-fg-muted">
              <span>
                {reviewed} of {total} findings saved
              </span>
              <span className="tabular-nums">{percent}%</span>
            </div>
            <div className="sidebar-progress-track mt-1.5">
              <div className="sidebar-progress-fill" style={{ width: `${percent}%` }} />
            </div>
          </div>
        </div>
        <CaseList
          units={manifest.units}
          current={data.current}
          selectedId={sampleId}
          onSelect={go}
          locked={saving}
        />
        <div className="space-y-3 border-t border-border px-4 py-3">
          <button
            type="button"
            onClick={() => void exportReview()}
            className="sidebar-btn h-8 w-full gap-2 text-xs text-fg-muted hover:text-fg"
          >
            <FaDownload className="h-3 w-3" />
            Export my review
          </button>
          {exportError && (
            <p role="alert" className="text-xs text-warn">
              {exportError}
            </p>
          )}
          <dl className="hidden grid-cols-[auto_1fr] items-center gap-x-2 gap-y-1.5 text-[11px] text-fg-dim md:grid">
            <dt className="flex gap-1">
              <Kbd>A</Kbd>
              <Kbd>B</Kbd>
              <Kbd>C</Kbd>
              <Kbd>D</Kbd>
            </dt>
            <dd>Rate the highlighted image</dd>
            <dt>
              <Kbd>Enter</Kbd>
            </dt>
            <dd>Save and go to the next unsaved finding</dd>
            <dt className="flex gap-1">
              <Kbd>←</Kbd>
              <Kbd>→</Kbd>
            </dt>
            <dd>Previous / next finding (also J / K)</dd>
            <dt>
              <Kbd>W</Kbd>
            </dt>
            <dd>Cycle window (drag the image to fine-tune)</dd>
          </dl>
        </div>
      </aside>
      <main className="min-h-0 flex-1 overflow-y-auto">
        <div className="mx-auto w-full max-w-6xl px-5 pt-5 md:px-8 md:pt-6">
          <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => manifest.units[index - 1] && go(manifest.units[index - 1].sample_id)}
                disabled={saving || index === 0}
                className="sidebar-btn h-8 w-8"
                title="Previous finding (←)"
                aria-label="Previous finding"
              >
                <FaChevronLeft className="h-3 w-3 text-fg-muted" />
              </button>
              <span className="min-w-20 text-center">
                <span className="text-lg font-semibold tabular-nums tracking-tight">
                  {index + 1}
                </span>
                <span className="text-sm font-light text-fg-muted">/{total}</span>
              </span>
              <button
                type="button"
                onClick={() => manifest.units[index + 1] && go(manifest.units[index + 1].sample_id)}
                disabled={saving || index === total - 1}
                className="sidebar-btn h-8 w-8"
                title="Next finding (→)"
                aria-label="Next finding"
              >
                <FaChevronRight className="h-3 w-3 text-fg-muted" />
              </button>
              <span className="ml-2 text-xs text-fg-dim">
                Finding {index + 1} of {total}
              </span>
            </div>
            <div className="flex items-center gap-3 text-xs">
              {saving && (
                <span role="status" className="rounded-[2px] bg-surface-2 px-2 py-1 text-fg-muted">
                  Saving… navigation is paused until the save is confirmed
                </span>
              )}
              {notice && !saving && (
                <span
                  role="status"
                  className="flex items-center gap-1.5 rounded-[2px] bg-signal-tint px-2 py-1 text-signal-text-tint"
                >
                  <FaCheck className="h-3 w-3" />
                  {notice}
                </span>
              )}
              {dirty && (
                <span className="text-fg-dim">
                  Unsaved changes ·{" "}
                  <button
                    type="button"
                    className="underline underline-offset-2 hover:text-fg"
                    onClick={() => {
                      if (window.confirm("Discard unsaved changes and reload the saved review?"))
                        window.location.reload();
                    }}
                  >
                    discard unsaved changes
                  </button>
                </span>
              )}
            </div>
          </div>
          {allSaved && (
            <section
              aria-label="Review complete"
              className="mb-5 flex flex-wrap items-center justify-between gap-3 rounded-[2px] border border-signal-stroke bg-signal-tint px-4 py-3"
            >
              <div className="flex items-center gap-3">
                <span className="flex h-7 w-7 items-center justify-center rounded-full bg-signal">
                  <FaCheck className="h-3.5 w-3.5 text-white" />
                </span>
                <div>
                  <p className="text-sm font-medium text-fg">All {total} findings saved</p>
                  <p className="text-xs text-fg-muted">
                    You can still reopen any finding to amend it. Earlier judgments stay in the
                    record.
                  </p>
                </div>
              </div>
              <Button variant="outline" onClick={() => void exportReview()}>
                Download review export
              </Button>
            </section>
          )}
          {!quick && (
            <p className="mb-4 text-xs text-fg-dim">
              Judge every returned image. Missing or unresolved cases stay in the review. Stop when
              your agreed time budget is reached; saved work can be resumed here.
            </p>
          )}
          <UnitForm
            key={`${sampleId}:${data.current[sampleId]?.revision ?? 0}`}
            unit={selected}
            manifest={manifest}
            manifestHash={data.manifest_sha256}
            saved={data.current[sampleId]}
            onSave={save}
            onDirty={setDirty}
            defaultInitials={
              sessionInitials || (data.history.at(-1)?.review.reviewer_initials ?? "")
            }
            shortcutsEnabled
            focusOnMount={focusRequest}
            token={token}
            reportOpen={reportOpen}
            onToggleReport={() => setReportOpen((value) => !value)}
          />
        </div>
      </main>
    </div>
  );
}

export default function KeyImageReview() {
  const { assessmentId = "" } = useParams<{ assessmentId: string }>();
  const { token, userId } = authService.getStoredAuth();
  const key = ["key-image-review", userId, assessmentId];
  const query = useQuery({
    queryKey: key,
    queryFn: async () => {
      const response = await axios.get<AssessmentView>(
        `${getApiBaseUrl()}/api/review/key-images/${encodeURIComponent(assessmentId)}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      return response.data;
    },
    enabled: !!token && !!assessmentId,
    retry: false,
    refetchOnWindowFocus: false,
    staleTime: Infinity,
  });
  if (query.isPending)
    return (
      <p role="status" className="p-8 text-sm text-fg-muted">
        Loading assigned review…
      </p>
    );
  if (!query.data || !token)
    return (
      <div className="space-y-3 p-8">
        <p role="alert">This review could not be loaded. Check your assignment or try again.</p>
        <Button onClick={() => void query.refetch()}>Retry</Button>
      </div>
    );
  return (
    <KeyImageAssessment
      key={`${assessmentId}:${query.data.manifest_sha256}`}
      data={query.data}
      token={token}
      onUpdated={async () => (await query.refetch({ throwOnError: true })).data}
    />
  );
}
